import React, { useState, useEffect } from 'react';
import { Fade, Reveal } from 'react-awesome-reveal';
import { Parallax, ParallaxProvider } from 'react-scroll-parallax';
import { useInView } from 'react-intersection-observer';
import { keyframes } from "@emotion/react";
import { WidgetsContext } from '../../components/widgets/core/context';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { FlipViewportAnimationSuppressContext } from '../widgets/flipBookContent/flipViewportAnimationSuppressContext';

const AnimationComponentView = ({ type, disabled, children, fraction = 0.1, ...rest }) => {
  if (disabled) {
    return children;
  }

  const context = React.useContext(WidgetsContext);

  var style = {
    height: "100%"
  };

  switch (type) {

    case 'fade': {
      const { delay, triggerOnce, duration, exit=false } = rest;
      return (
        <FadeAnimation
          delay={delay}
          fraction={fraction}
          exit={exit}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </FadeAnimation>
      )
    }


    case 'slide': {
      const { duration, delay, direction, distance, triggerOnce, exit=false } = rest;
      
      return (
        <SlideAnimation
          delay={delay}
          direction={direction}
          exit={exit}
          fraction={fraction}
          distance={distance}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </SlideAnimation>
      )
    }

    case 'spin': {
      const { duration, delay, direction, count, triggerOnce, exit=false } = rest;
      return (
        <SpinAnimation
          delay={delay}
          direction={direction}
          exit={exit}
          fraction={fraction}
          count={count}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </SpinAnimation>
      )
    }

    case 'bounce': {
      const { delay, triggerOnce, direction, duration, exit=false } = rest;
      return (
        <BounceAnimation
          delay={delay}
          // direction={direction}
          fraction={fraction}
          exit={exit}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </BounceAnimation>
      )
    }

    case 'arc': {
      const { delay, triggerOnce, direction, duration, exit=false } = rest;
      return (
        <ArcAnimation
          delay={delay}
          direction={direction}
          exit={exit}
          fraction={fraction}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </ArcAnimation>
      )
    }

    case 'flip': {
      const { delay, triggerOnce, direction, duration, exit=false } = rest;
      return (
        <FlipAnimation
          delay={delay}
          fraction={fraction}
          direction={direction}
          exit={exit}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </FlipAnimation>
      )
    }

    case 'expand': {
      const { delay, triggerOnce, duration, exit=false } = rest;
      return (
        <ExpandAnimation
          delay={delay}
          triggerOnce={triggerOnce}
          exit={exit}
          fraction={fraction}
          duration={duration}
          style={style}
        >
          {children}
        </ExpandAnimation>
      )
    }

    case 'puffin': {
      const { delay, triggerOnce, duration, exit=false } = rest;
      return (
        <PuffAnimation
          delay={delay}
          triggerOnce={triggerOnce}
          exit={exit}
          fraction={fraction}
          duration={duration}
          style={style}
        >
          {children}
        </PuffAnimation>
      )
    }

    case 'turn': {
      const { delay, triggerOnce, direction, duration } = rest;
      return (
        <TurnInAnimation
          delay={delay}
          triggerOnce={triggerOnce}
          fraction={fraction}
          direction={direction}
          duration={duration}
          style={style}
        >
          {children}
        </TurnInAnimation>
      )
    }

    case 'reveal': {
      const { delay, triggerOnce, direction, duration, exit=false } = rest;
      return (
        <RevealAnimation
          delay={delay}
          triggerOnce={triggerOnce}
          fraction={fraction}
          exit={exit}
          direction={direction}
          duration={duration}
          style={style}
        >
          {children}
        </RevealAnimation>
      )
    }

    case 'parallax': {
      const { direction, speed, angle, reverse } = rest;

      return (
        <ParallaxAnimation
          type={type}
          direction={direction}
          speed={speed}
          angle={angle}
          reverse={reverse}
          style={style}
        >
          {children}
        </ParallaxAnimation>
      )
    }

  }

  return children;
}

const FadeAnimation = ({ duration, delay, fraction, children, triggerOnce, style, exit=false }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);

  if (exit) {

    const { ref, inView, entry } = useInView({
      threshold: fraction,
      triggerOnce: triggerOnce,
      skip: suppressFlipViewport,
    });

    return (
      <div ref={ref} style={style}>
        <div style={{
          opacity: inView ? 0 : 1,
          transition: `opacity ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
          height: "100%"
        }}>
          {children}
        </div>
      </div>
    )
  }

  if (suppressFlipViewport) {
    return <div style={style}>{children}</div>;
  }

  return (
    <Fade fraction={fraction} triggerOnce={triggerOnce} duration={duration * 1000} delay={delay * 1000} style={style}>
      {children}
    </Fade>
  )
}

const SlideAnimation = ({ duration, triggerOnce, delay, direction, fraction, distance, children, style, exit=false }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);
  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce,
    skip: suppressFlipViewport,
  });

  const animationVlaue = generateKeyFrameForSlide(direction, distance);

  if(exit) {

    const exitAnimationVlaue = generateKeyFrameForSlideOut(direction, distance);

    return (
      <div ref={ref} style={style}>
      <style>
        {exitAnimationVlaue}
      </style>
      <div style={{
        animation: inView ? `slideOutAnimation ${duration}s ease-out ${delay}s` : 'none',
        height: "100%"
      }}>
        {children}
      </div>
    </div>
    )
  }

  return (
    <div ref={ref} style={style}>
      <div style={{
        transform: inView ? animationVlaue.to.transform : animationVlaue.from.transform,
        transition:  `transform ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"
      }}>
        {children}
      </div>
    </div>
  )
}

const SpinAnimation = ({ duration, delay, triggerOnce, fraction, direction, count, children, style }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);
  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce,
    skip: suppressFlipViewport,
  });

  const animationValue = generateKeyFrameForSpin(direction, count);
  return (
    <div ref={ref} style={style}>
      <div style={{
        opacity: inView ? animationValue.to.opacity : animationValue.from.opacity,
        transform: inView ? animationValue.to.transform : animationValue.from.transform,
        transition: `transform ${inView ? duration : 0}s ease-out ${inView ? delay : 0}s`,
        height: "100%"
      }}>
        {children}
      </div>
    </div>
  );
}

const BounceAnimation = ({ duration, delay, triggerOnce, fraction, children, style }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);
  const { ref, inView } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce,
    skip: suppressFlipViewport,
  });

  const animationValue = generateKeyFrameForBounce();
  return (
    <div ref={ref} style={style}>
      <style>
        {animationValue}
      </style>
      <div style={{
        animation: inView ? `bounceAnimation ${duration}s ease-out ${delay}s` : 'none',
        height: "100%"
      }}>
        {children}
      </div>
    </div>
  );
}

const ArcAnimation = ({ duration, delay, triggerOnce, fraction, direction, children, style, exit=false }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);
  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce,
    skip: suppressFlipViewport,
  });

  const animationValue = generateKeyFrameForArc(direction);

  if(exit) {
    return (
      <div ref={ref} style={style}>
      <div style={{
        transform: inView ? animationValue.from.transform : animationValue.to.transform,
        scale: inView ? animationValue.from.scale : animationValue.to.scale,
        opacity: inView ? animationValue.from.opacity : animationValue.to.opacity,
        transition:  `all ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"
      }}>
        {children}
      </div>
    </div>
    )
  }

  return (
    <div ref={ref} style={style}>
      <div style={{
        transform: inView ? animationValue.to.transform : animationValue.from.transform,
        scale: inView ? animationValue.to.scale : animationValue.from.scale,
        opacity: inView ? animationValue.to.opacity : animationValue.from.opacity,
        transition:  `all ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"
      }}>
        {children}
      </div>
    </div>
  )
}

const FlipAnimation = ({ duration, delay, triggerOnce, fraction, direction, children, style, exit=false }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);

  if (suppressFlipViewport) {
    return <div style={{ height: '100%', ...style }}>{children}</div>;
  }

  if(exit) {
    return (
      <Reveal fraction={fraction} style={{ animationTimingFunction: "ease", ...style }} triggerOnce={triggerOnce} keyframes={generateKeyFrameForFlipOut(direction)} duration={duration * 1000} delay={delay * 1000}>
      {children}
    </Reveal>
    )
  }

  return (
    <Reveal fraction={fraction} style={{ animationTimingFunction: "ease", ...style }} triggerOnce={triggerOnce} keyframes={generateKeyFrameForFlip(direction)} duration={duration * 1000} delay={delay * 1000}>
      {children}
    </Reveal>
  )
}

const ExpandAnimation = ({ duration, delay, triggerOnce, fraction, children, style, exit=false }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);
  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce,
    skip: suppressFlipViewport,
  });

  const animationValue = generateKeyFrameForExpand();

  if(exit) {
    return (
      <div ref={ref} style={style}>
      <div
        style={{
          transform: inView ? animationValue.from.transform : animationValue.to.transform,
          opacity: inView ? animationValue.from.opacity : animationValue.to.opacity,
          transition: `all ${inView ? duration : 0}s ease-out ${inView ? delay : 0}s`,
          height: '100%',
        }}
      >
        {children}
      </div>
    </div>
    )
  }
  return (
    <div ref={ref} style={style}>
      <div
        style={{
          transform: inView ? animationValue.to.transform : animationValue.from.transform,
          opacity: inView ? animationValue.to.opacity : animationValue.from.opacity,
          transition: `all ${inView ? duration : 0}s ease-out ${inView ? delay : 0}s`,
          height: '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const PuffAnimation = ({ duration, delay, triggerOnce, children, fraction, style, exit=false }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);

  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce,
    skip: suppressFlipViewport,
  });

  const animationVlaue = generateKeyFrameForPuff()

  if(exit) {
    return (
      <div ref={ref} style={style}>
      <div style={{
        transform: inView ? animationVlaue.from.transform : animationVlaue.to.transform,
        opacity: inView ? 0 : 1,
        transition:  `all ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"
      }}>
        {children}
      </div>
    </div>
    )
  }

  return (
    <div ref={ref} style={style}>
      <div style={{
        transform: inView ? animationVlaue.to.transform : animationVlaue.from.transform,
        transition:  `transform ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"
      }}>
        {children}
      </div>
    </div>
  )
}

const TurnInAnimation = ({ duration, delay, triggerOnce, fraction, direction, children, style }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);
  if (suppressFlipViewport) {
    return <div style={{ height: '100%', ...style }}>{children}</div>;
  }

  return (
    <Reveal fraction={fraction} style={{ animationTimingFunction: "ease", ...style }} triggerOnce={triggerOnce} keyframes={generateKeyFrameForTurnIn(direction)} duration={duration * 1000} delay={delay * 1000}>
      {children}
    </Reveal>
  )
}

const RevealAnimation = ({ duration, delay, fraction, triggerOnce, direction, children, style, exit=false }) => {
  const suppressFlipViewport = React.useContext(FlipViewportAnimationSuppressContext);
  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce,
    skip: suppressFlipViewport,
  });

  var animationVlaue = generateKeyFrameForReveal(direction);

  if(exit) {
    animationVlaue = generateKeyFrameForRevealOut(direction);
    return (
      <div ref={ref} style={style}>
        <div style={{
          clipPath: inView ? animationVlaue.to : animationVlaue.from,
          transition:  `clip-path ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
          height: "100%"
  
        }}>
          {children}
        </div>
      </div>
    )
  }

  return (
    <div ref={ref} style={style}>
      <div style={{
        clipPath: inView ? animationVlaue.to : animationVlaue.from,
        transition:  `clip-path ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"

      }}>
        {children}
      </div>
    </div>
  )
}

const ParallaxAnimation = ({ direction, speed, angle, reverse, children }) => {
  return (
    <Parallax
      shouldAlwaysCompleteAnimation={false}
      translateX={getTranslateX(direction, speed, angle, reverse)}
      translateY={getTranslateY(direction, speed, angle, reverse)}
      style={{height:"100%"}}
    >
      {children}
    </Parallax>
  )
}

const generateKeyFrameForSlideOut = (direction, distance, count = 1) => {

  switch (direction) {
    case 'left':
      return `
      @keyframes slideOutAnimation {
        from {
          opacity: 1;
          transform: 'translateX(0)'
        }
        to {
          opacity: 0;
          transform: translateX(-${distance}px)
        }
      }`;

    case 'right':
      return `
      @keyframes slideOutAnimation {
        from {
          opacity: 1;
          transform: 'translateX(0)'
        }
        to {
          opacity: 0;
          transform: translateX(${distance}px)
        }
      }`;

    case 'bottom':
      return `
      @keyframes slideOutAnimation {
        from {
          opacity: 1;
          transform: 'translateY(0)'
        }
        to {
          opacity: 0;
          transform: translateY(${distance}px)
        }
      }`;

    case 'top':
      return `
    @keyframes slideOutAnimation {
      from {
        opacity: 1;
        transform: 'translateY(0)'
      }
      to {
        opacity: 0;
        transform: translateY(-${distance}px)
      }
    }`;
  }
};


const generateKeyFrameForSlide = (direction, distance, count = 1) => {
  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = {
        from: {
          transform: `translateX(-${distance}px)`
        },
        to: {
          transform: 'translateX(0)'
        }
      };
      break;

    case 'left':
      animationKeyframe = {
        from: {
          transform: `translateX(${distance}px)`
        },
        to: {
          transform: 'translateX(0)'
        }
      };
      break;

    case 'top':
      animationKeyframe = {
        from: {
          opacity: 1,
          transform: `translateY(${distance}px)`
        },
        to: {
          opacity: 1,
          transform: 'translateY(0)'
        }
      };
      break;

    case 'bottom':
      animationKeyframe = {
        from: {
          transform: `translateY(-${distance}px)`
        },
        to: {
          transform: 'translateY(0)'
        }
      };
      break;

    default:
      break;
  }

  return animationKeyframe;
};


const getTranslateX = (direction, speed, angle, reverse) => {
  let translate = null;

  var end = 100;
  var degree = 0;
  switch (direction) {
    case 'vertical':
      degree = 0;
      end = 0;
      break;

    case 'horizontal':
      degree = 360;
      break;

    case 'angle':
      degree = angle - 90;
      break;

    default:
      break;
  }

  var x = Number.parseFloat(Math.cos(Math.PI * 2 * degree / 360)).toFixed(2);
  const delta = end * x * (speed / 2);
  translate = reverse ? [`${delta}px`, `${-delta}px`] : [`${-delta}px`, `${delta}px`];

  return translate;
}

const getTranslateY = (direction, speed, angle, reverse) => {
  let translate = null;

  var end = 100;
  var degree = 0;
  switch (direction) {
    case 'vertical':
      degree = 270;
      break;

    case 'horizontal':
      degree = 0;
      end = 0;
      break;

    case 'angle':
      degree = angle - 90;
      break;

    default:
      break;
  }

  var y = Number.parseFloat(Math.sin(Math.PI * 2 * degree / 360)).toFixed(2);
  const delta = end * y * (speed / 2);
  translate = reverse ? [`${delta}px`, `${-delta}px`] : [`${-delta}px`, `${delta}px`];

  return translate;
}

const generateKeyFrameForSpin = (direction, count) => {
  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = {
        from: {
          opacity: 0,
          transform: 'scale(0) rotate(0deg)'
        },
        to: {
          opacity: 1,
          transform: `scale(1) rotate(${360 * count}deg)`
        }
      };
      break;

    case 'left':
      animationKeyframe = {
        from: {
          opacity: 0,
          transform: `scale(0) rotate(${360 * count}deg)`
        },
        to: {
          opacity: 1,
          transform: 'scale(1) rotate(0deg)'
        }
      };
      break;

    default:
      break;
  }

  return animationKeyframe;
}

const generateKeyFrameForBounce = () => {
  return `
    @keyframes bounceAnimation {
      from {
        opacity: 0;
        transform: scale(0.1);
      }
      60% {
        opacity: 1;
        transform: scale(1.2);
      }
      80% {
        transform: scale(0.9);
      }
      to {
        transform: scale(1);
      }
    }`;
};

const generateKeyFrameForArc = (direction) => {
  let animationValue = null;

  switch (direction) {
    case 'right':
      animationValue = {
        from: {
          opacity: 0,
          scale: 0.5,
          transform: 'perspective(1500px) rotateY(180deg) translateZ(300px)'
        },
        to: {
          opacity: 1,
          scale: 1,
          transform: 'perspective(1500px) rotateY(0deg) translateZ(0px)'
        }
      };
      break;

    case 'left':
      animationValue = {
        from: {
          opacity: 0,
          scale: 0.5,
          transform: 'perspective(1500px) rotateY(-180deg) translateZ(300px)'
        },
        to: {
          opacity: 1,
          scale: 1,
          transform: 'perspective(1500px) rotateY(0deg) translateZ(0px)'
        }
      };
      break;

    default:
      break;
  }

  return animationValue;
};


const generateKeyFrameForArcOut = (direction) => {
  switch (direction) {

    case 'right':
      return `
      @keyframes arcAnimation {
        from {
          opacity: 1,
          scale: 1,
          transform: 'perspective(1500px) rotateY(0deg) translateZ(0px)'
        }
        to {
          opacity: 0,
          scale: 0.5,
          transform: 'perspective(1500px) rotateY(180deg) translateZ(300px)'
        }
      }`;

    case 'left':
      return `
      @keyframes arcAnimation {
        from {
          opacity: 1,
          scale: 1,
          transform: 'perspective(1500px) rotateY(0deg) translateZ(0px)'
        }
        to {
          opacity: 0,
          scale: 0.5,
          transform: 'perspective(1500px) rotateY(-180deg) translateZ(300px)'
        }
      }`;
  }
};


const generateKeyFrameForFlip = (direction) => {
  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = keyframes`
      from {
        transform-origin: left;
        transform: rotateY(90deg);
      }
      to {
        transform: rotateY(0deg);
      }`;
      break;

    case 'left':
      animationKeyframe = keyframes`
      from {
        transform-origin: right;
        transform: rotateY(-90deg);
      }
      to {
        transform: rotateY(0deg);
      }
      `;
      break;

    case 'top':
      animationKeyframe = keyframes`
      from {
        transform-origin: bottom;
        transform: rotateX(90deg);
      }
      to {
        transform: rotateX(0deg);
      }`;
      break;

    case 'bottom':
      animationKeyframe = keyframes`
        from {
          transform-origin: top;
          transform: rotateX(-90deg);
        }
        to {
          transform: rotateX(0deg);
        }
        `;
      break;

    default:
      break;
  }

  return animationKeyframe;
}


const generateKeyFrameForFlipOut = (direction) => {
  let animationKeyframe = null;

  switch (direction) {
    case 'left':
      animationKeyframe = keyframes`
      from {
        transform: rotateY(0deg);
      }
      to {
        transform-origin: left;
        transform: rotateY(90deg);
      }`;
      break;

    case 'right':
      animationKeyframe = keyframes`
      from {
        transform: rotateY(0deg);
      }
      to {
        transform-origin: right;
        transform: rotateY(-90deg);
      }
      `;
      break;

    case 'bottom':
      animationKeyframe = keyframes`
      from {
        transform: rotateX(0deg);
      }
      to {
        transform-origin: bottom;
        transform: rotateX(90deg);
      }`;
      break;

    case 'top':
      animationKeyframe = keyframes`
        from {
          transform: rotateX(0deg);
        }
        to {
          transform-origin: top;
          transform: rotateX(-90deg);
        }
        `;
      break;

    default:
      break;
  }

  return animationKeyframe;
}

const generateKeyFrameForExpand = () => {
  return {
    from: {
      opacity: 0,
      transform: 'scale(0)',
    },
    to: {
      opacity: 1,
      transform: 'scale(1)',
    },
  };
};

const generateKeyFrameForPuff = () => {
  let animationKeyframe = {
    from: {
      transform: 'scale(2.5)'
    },
    to: {
      opacity: 1,
      transform: 'scale(1)'
    }
  };

  return animationKeyframe;
};

const generateKeyFrameForTurnIn = (direction) => {

  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = keyframes`
      0% {
        opacity: 0;
        transform: translate(200%, -200%) rotate(45deg);
      }
      100% {
        opacity: 1;
        transform: translate(0, 0) rotate(0deg);
      }`;
      break;

    case 'left':
      animationKeyframe = keyframes`
      from {
        opacity: 0;
        transform: translate(-200%, -200%) rotate(-45deg);
      }
      to {
        opacity: 1;
        transform: translate(0, 0) rotate(0deg);
      }`;
      break;

    default:
      break;
  }


  return animationKeyframe;
}

const generateKeyFrameForReveal = (direction) => {

  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = {
      from:'polygon(0 0, 0 0, 0% 100%, 0% 100%)',
      to :'polygon(0 0, 100% 0, 100% 100%, 0% 100%)'
    }
      break;

    case 'left':
      animationKeyframe = {
      from :'polygon(100% 0, 100% 0, 100% 100%, 100% 100%)',
      to :'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
    };
      break;


    case 'top':
      animationKeyframe = {
      from: 'polygon(0 100%, 100% 100%, 100% 100%, 0 100%)',
      to :'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
    }
      break;


    case 'bottom':
      animationKeyframe = {
      from :'polygon(0 0, 100% 0, 100% 0, 0 0)',
      to :'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
    };
      break;

    default:
      break;
  }

  return animationKeyframe;
}

const generateKeyFrameForRevealOut = (direction) => {

  let animationKeyframe = null;

  switch (direction) {
    case 'left':
      animationKeyframe = {
      from:'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
      to :'polygon(0 0, 0 0, 0% 100%, 0% 100%)'
    }
      break;

    case 'right':
      animationKeyframe = {
      from :'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
      to :'polygon(100% 0, 100% 0, 100% 100%, 100% 100%)'
    };
      break;


    case 'bottom':
      animationKeyframe = {
      from: 'polygon(0 100%, 100% 100%, 100% 100%, 0 100%)',
      to :'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
    }
      break;


    case 'top':
      animationKeyframe = {
      from :'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
      to :'polygon(0 0, 100% 0, 100% 0, 0 0)'
    };
      break;

    default:
      break;
  }


  return animationKeyframe;
}

const animationEquals = function (prevProps, nextProps) {
  const paths = ['preview'];
  const next = omit(nextProps, paths);
  const previous = omit(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const AnimationComponent = React.memo(AnimationComponentView, animationEquals);

export const ParallaxContainer = ({ scrollRef, children }) => {

  if (children) {
    return (
      <ParallaxProvider scrollContainer={scrollRef} >
        {children}
      </ParallaxProvider>
    )
  }

  return children;
}
